// ── DashboardPage ──
import { useState, useEffect } from "react";
import { B, alertChip, alertsFor, statusColor } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Badge, Card, ErrorMsg, PageLink, SectionHeader, Spinner, Td, TdNum, Th, ThNum } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";
import { PortfolioHeatMap } from "./PortfolioHeatMap";
// The same definition the Tax Monitoring page's Coverage card uses. The coverage
// gap on this dashboard and the one on that page cannot disagree, because there
// is only one of them.
import { authState, isUnmonitored } from "../../../shared/taxCoverage";

export const DashboardPage = () => {
    // The to-do cards (sweeps, participants, draws, ACH, covenants and the
    // monitoring registers) live on the Action Center (ActionCenterPage.js);
    // this page keeps the portfolio facts.
    const { api, currentUser, rowNav } = useApp();
  const [dash, setDash] = useState(null);
  const [terms, setTerms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [portfolioSort, setPortfolioSort] = useState({ col: 'TotalExposure', dir: 'desc' });
  const cardTitle = { fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, paddingBottom:6, borderBottom:"1.5px solid "+B.cyanLight };  
  const cardRow     = { display:"flex", justifyContent:"space-between", fontSize:12, fontWeight:400, marginBottom:6 };
  const fundCell    = { fontSize:12, fontWeight:400, padding:"3px 0", textAlign:"left" };
  const fundCellNum = { fontSize:12, fontWeight:400, padding:"3px 0", textAlign:"right", fontVariantNumeric:"tabular-nums" };
  const fundHead    = { ...fundCell,    fontWeight:700, color:"var(--color-text-secondary)" };
  const fundHeadNum = { ...fundCellNum, fontWeight:700, color:"var(--color-text-secondary)" };


  const load = () => {
    setLoading(true);
    Promise.all([api.get("getDashboard"), api.get("getTerms"), api.get("getAccounts")])
      .then(([d, tm, ac]) => { setDash(d); setTerms(Array.isArray(tm) ? tm : []); setAccounts(Array.isArray(ac) ? ac : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);
  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const {
    accountStats: as,
    loanStats: ls,
    upcomingPayments: up,
    overduePayments = [],
    loansByStatus,
    portfolioByAccount,
    delinquencyBuckets: db,
    portfolioTotals: pt, 
    fundBalances = []
  } = dash;

  // ── Delinquency aging ──────────────────────────────────────────────────
  // The ramp is AGE_BANDS, the same four steps the covenant card uses. Two cards
  // that both mean "how many days late" should not say it in two palettes — and
  // the old one here started at #ffb20d, which is 1.81:1 against white: fine as
  // a wide bar, unreadable as a 10px swatch beside a label.
  const agingBuckets = [
    { label: "1–30 days",  value: db.bucket_0_30,   color: AGE_BANDS[0].color },
    { label: "31–60 days", value: db.bucket_31_60,  color: AGE_BANDS[1].color },
    { label: "61–90 days", value: db.bucket_61_90,  color: AGE_BANDS[2].color },
    { label: "over 90",    value: db.bucket_90plus, color: AGE_BANDS[3].color },
  ];
  const totalDelinquent = agingBuckets.reduce((s, b) => s + (b.value || 0), 0);

  // ── What the Overdue and Upcoming Payments report would show ───────────
  // TWO DIFFERENT UNITS, so both get named. The aging buckets above count
  // LOANS, aged from the oldest unpaid scheduled date on each. These count
  // PAYMENTS — one loan three months behind is one loan and three payments.
  // Labelling either of them a bare number would invite the reader to compare
  // them, and they do not compare.
  //
  // `overduePayments` was already arriving in the dashboard payload and nothing
  // read it. It is the report's "Overdue" section, pre-filtered server-side.
  const overdueAmount = overduePayments.reduce(
    (s, p) => s + Math.max(0, (Number(p.Amount) || 0) - (Number(p.TotalReceived) || 0)), 0);
  const overdueAccounts = new Set(overduePayments.map(p => p.AccountName)).size;
  // ── Funding in Process — one row per account whose account-level status is "Funding in Progress"
  //    (from getAccounts), with term detail (from getTerms) when a loan+term already exist.
  //    Accounts with no loan/term yet still show (so they're reachable right after creation). ──
  const fipAccounts        = accounts.filter(a => a.accountstatus === "Funding in Progress");
  const fipTermsByAccount  = terms.reduce((m, t) => { (m[t.accountid] = m[t.accountid] || []).push(t); return m; }, {});
  const fundingInProgress  = fipAccounts.flatMap(a => {
    const ts = fipTermsByAccount[a.id] || [];
    return ts.length
      ? ts.map(t => ({ key:"t"+t.id, accountid:a.id, companyname:a.companyname, collateraltype:t.collateraltype, facility_type:t.facility_type, commitment:t.commitment, fundingamount:t.fundingamount }))
      : [{ key:"a"+a.id, accountid:a.id, companyname:a.companyname, collateraltype:null, facility_type:null, commitment:0, fundingamount:0 }];
  });
  const fipCommitmentTotal = fundingInProgress.reduce((s, t) => s + (parseFloat(t.commitment)   || 0), 0);
  const fipFundingTotal    = fundingInProgress.reduce((s, t) => s + (parseFloat(t.fundingamount) || 0), 0);

  // An account in funding with NO TERM on it yet is a different kind of item
  // from one being negotiated: nobody has entered what is being lent. It is the
  // one split on this tile worth acting on, so it is counted rather than
  // buried in a row that shows zeros.
  const fipWithTerms  = fipAccounts.filter(a => (fipTermsByAccount[a.id] || []).length > 0).length;
  const fipNoTerms    = fipAccounts.length - fipWithTerms;
  const fipFacilities = fundingInProgress.filter(r => String(r.key).charAt(0) === "t").length;

  // ── Accounts by status (from getAccounts, already loaded above) ──
  //
  // THE BAR LENGTHS ARE A SHARE OF WHAT THE CARD SHOWS, and that has to be
  // computed from the rows it actually renders. It used to divide by
  // `accounts.length` — EVERY account, including the Paid Off and Terminated
  // ones this list leaves out. With a book that is mostly paid off, ten accrual
  // accounts out of fourteen listed drew a bar a quarter of the way across
  // instead of three quarters, and nothing on the card explained the gap.
  //
  // Summing the same array that renders is the point: a status added to or
  // dropped from ACCT_STATUS_ORDER moves the bars with it, and the total can
  // never describe a different set than the rows above it.
  const ACCT_STATUS_ORDER = ["Accrual", "Non Accrual", "Funding in Progress", "Charged Off"];
  const accountsByStatus = ACCT_STATUS_ORDER
    .map(status => ({ status, count: accounts.filter(a => a.accountstatus === status).length }))
    .filter(g => g.count > 0);
  const accountsByStatusShown = accountsByStatus.reduce((s, g) => s + g.count, 0);

  // ── Loans by status ──
  // Same fault, reached a different way: the card filtered "Paid Off" out at
  // render time and then divided by ls.Total, which still counted them. The
  // filter now happens once, here, and the denominator is built from its result.
  const loansByStatusShown = (loansByStatus || [])
    .filter(s => (s.loanstatus || "").trim().toLowerCase() !== "paid off");
  const loansByStatusTotal = loansByStatusShown.reduce((s, r) => s + (Number(r.Count) || 0), 0);


  // ── Portfolio sort ─────────────────────────────────────────────────────
  const toggleSort = col => setPortfolioSort(prev =>
    prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: col === 'TotalExposure' || col === 'LoanCount' ? 'desc' : 'asc' }
  );
    const activePortfolio = portfolioByAccount.filter(a => a.accountstatus !== 'Paid Off');
    const sortedPortfolio = [...activePortfolio].sort((a, b) => {
      const { col, dir } = portfolioSort;
      const av = a[col], bv = b[col];
      const cmp = typeof av === 'number' ? av - bv : (av || '').localeCompare(bv || '');
      return dir === 'asc' ? cmp : -cmp;
    });

    const portfolioTotalsFor = arr => ({
        count:      arr.length,
        loans:      arr.reduce((s, a) => s + (a.LoanCount || 0), 0),
        commitment: arr.reduce((s, a) => s + (a.Commitment || 0), 0),
        collateral: arr.reduce((s, a) => s + (a.Collateral || 0), 0),
        exposure:   arr.reduce((s, a) => s + (a.TotalExposure || 0), 0),
        bs:     arr.reduce((s, a) => s + (a.BSExposure || 0), 0),
      });
    const portfolioGroupOrder = ["Accrual", "Non Accrual", "Charged Off"];
    const portfolioGroups = [
    ...portfolioGroupOrder,
    ...[...new Set(sortedPortfolio.map(a => a.accountstatus))].filter(s => !portfolioGroupOrder.includes(s)),
  ]
    .map(status => ({ status, rows: sortedPortfolio.filter(a => a.accountstatus === status) }))
    .filter(g => g.rows.length > 0);

  const portfolioGrand = portfolioTotalsFor(sortedPortfolio);

  const pfNumCell  = { textAlign:"right", padding:"9px 12px", fontSize:13, borderBottom:"0.5px solid var(--color-border-tertiary)", fontVariantNumeric:"tabular-nums" };
  const pfSubNum   = { textAlign:"right", padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark, fontVariantNumeric:"tabular-nums" };
  const pfGrandNum = { textAlign:"right", padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark, fontVariantNumeric:"tabular-nums" };
  const portfolioTotalLoans    = portfolioByAccount.reduce((s, a) => s + (a.LoanCount || 0), 0);
  const portfolioTotalCommitment = portfolioByAccount.reduce((s, a) => s + (a.Commitment || 0), 0);
  const portfolioTotalCollateral = portfolioByAccount.reduce((s, a) => s + (a.Collateral || 0), 0);
  const portfolioTotalExposure = portfolioByAccount.reduce((s, a) => s + (a.TotalExposure || 0), 0);
  const fundBalanceTotal = fundBalances.reduce((s, f) => s + (f.allocatedbalance || 0), 0);
  const fundCommitmentTotal = fundBalances.reduce((s, f) => s + (f.allocatedcommitment || 0), 0);
  const fundCollateralTotal = fundBalances.reduce((s, f) => s + (f.allocatedcollateral || 0), 0);
  const fundBSCommitTotal = fundBalances.reduce((s, f) => s + (f.allocatedbscommitment || 0), 0);
  const fundBSTotal     = fundBalances.reduce((s, f) => s + (f.allocatedbs     || 0), 0);
  const fundLoanTotal = fundBalances.reduce((s, f) => s + (f.loancount || 0), 0);
  // Sort indicator helper
  const SortIcon = ({ col }) => {
    if (portfolioSort.col !== col) return <span style={{ opacity:0.3, marginLeft:4 }}>↕</span>;
    return <span style={{ marginLeft:4 }}>{portfolioSort.dir === 'asc' ? '↑' : '↓'}</span>;
  };
  // Clickable header for portfolio table
  const ThSort = ({ col, children, right }) => (
    <th
      onClick={() => toggleSort(col)}
      style={{
        textAlign: right ? 'right' : 'left',
        padding: "8px 12px", fontSize: 12, fontWeight: 500,
        color: "var(--color-text-secondary)",
        borderBottom: "0.5px solid var(--color-border-tertiary)",
        background: "var(--color-background-secondary)",
        whiteSpace: "nowrap", cursor: "pointer", userSelect: "none"
      }}
    >
      {children}<SortIcon col={col} />
    </th>
  );






  return <>
    <SectionHeader title={"Welcome back, " + currentUser.name} />

 {/* STAT CARDS */}

  {/* ── Row 1: Active Portfolio Total + Balances by Fund + Funding in Process ──
      Funding in Process stopped being a table and became a tile, so the width
      it held for four columns of rows is width it no longer uses — a tile is
      one number, a bar and a link at any size. That space goes to Balances by
      Fund, which carries six numeric columns per row and was the card actually
      short of room.

      minmax(240px, …) on the tile rather than a bare fraction: 240 is the
      floor the other tiles on this page already use, and below it the split
      bar and its two key lines start wrapping. The fraction says what it wants,
      the minimum says what it will not go under. */}
  <div style={{ display:"grid", gridTemplateColumns:"1.15fr 2.9fr minmax(240px, 0.95fr)", gap:16, marginBottom:16 }}>

    {/* 1 · Active Portfolio Total */}
    <Card style={{ padding:14 }}>
      <div style={cardTitle}>Active Portfolio Total — {as.Active} Accounts</div>
      {[
        ["Commitment",        fmt(pt.TotalCommitment)],
        ["Collateral",        fmt(pt.TotalCollateralValue)],
        ["Loan Balance",      fmt(ls.PortfolioValue)],
        // Our share of the commitment, on the same scaler as the collateral
        // figure above. Sits beside Loan Balance so the pair reads gross then
        // ours, twice over.
        ["BS Commitment",     fmt(pt.TotalBSCommitment)],
        ["BS Loan Balance",   fmt(pt.TotalBSLoanBal)],
      ].map(([label,value]) => (
        <div key={label} style={cardRow}>
          <span style={{ color:"var(--color-text-secondary)" }}>{label}</span>
          <span>{value}</span>
        </div>
      ))}
    </Card>

    {/* 2 · Balances by Fund */}
    <Card style={{ padding:14, overflowX:"auto" }}>
      <div style={cardTitle}>Balances by Fund</div>
      {fundBalances.length === 0
        ? <div style={{ fontSize:12, color:"var(--color-text-secondary)" }}>No fund ownership recorded.</div>
        : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
            <thead><tr>
              <td style={fundHead}>Fund</td>
              <td style={fundHeadNum}>Loans</td>
              <td style={fundHeadNum}>Commitment</td>
              <td style={fundHeadNum}>Collateral</td>
              <td style={fundHeadNum}>Loan Balance</td>
              <td style={fundHeadNum}>BS Commitment</td>
              <td style={fundHeadNum}>BS Loan Balance</td>
            </tr></thead>
            <tbody>
              {fundBalances.map(f => (
                <tr key={f.id}
                  {...rowNav("all-loans", { fundname: f.fundname })}
                  style={{ cursor:"pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td style={fundCell}><PageLink page="all-loans" params={{ fundname: f.fundname }}>{f.fundname}</PageLink></td>
                  <td style={fundCellNum}>{f.loancount || 0}</td>
                  <td style={fundCellNum}>{fmt(f.allocatedcommitment || 0)}</td>
                  <td style={fundCellNum}>{fmt(f.allocatedcollateral || 0)}</td>
                  <td style={fundCellNum}>{fmt(f.allocatedbalance || 0)}</td>
                  <td style={fundCellNum}>{fmt(f.allocatedbscommitment || 0)}</td>
                  <td style={fundCellNum}>{fmt(f.allocatedbs || 0)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...fundCell,    borderTop:"1px solid var(--color-border-tertiary)" }}>Total</td>
                <td style={{ ...fundCellNum, borderTop:"1px solid var(--color-border-tertiary)" }}>{fundLoanTotal}</td>
                <td style={{ ...fundCellNum, borderTop:"1px solid var(--color-border-tertiary)" }}>{fmt(fundCommitmentTotal)}</td>
                <td style={{ ...fundCellNum, borderTop:"1px solid var(--color-border-tertiary)" }}>{fmt(fundCollateralTotal)}</td>
                <td style={{ ...fundCellNum, borderTop:"1px solid var(--color-border-tertiary)" }}>{fmt(fundBalanceTotal)}</td>
                <td style={{ ...fundCellNum, borderTop:"1px solid var(--color-border-tertiary)" }}>{fmt(fundBSCommitTotal)}</td>
                <td style={{ ...fundCellNum, borderTop:"1px solid var(--color-border-tertiary)" }}>{fmt(fundBSTotal)}</td>
              </tr>
            </tbody>
          </table>}
    </Card>

    {/* 3 · Funding in Process — A TILE, NOT A TABLE.
        This was a row per facility with a totals line. It reads fine at four
        rows and falls apart at forty: the card grows without limit, pushes
        every other card down the page, and a dashboard that reflows depending
        on how good a quarter the BDOs had is not a dashboard.

        A tile is a fixed size whatever the pipeline does. The rows still
        exist — the Accounts page carries the full Funding in Progress
        worklist, with the edit and delete actions this card never had. */}
    <FundingInProcess accounts={fipAccounts.length} facilities={fipFacilities}
                      withTerms={fipWithTerms} noTerms={fipNoTerms}
                      commitment={fipCommitmentTotal} funding={fipFundingTotal}
                      title={cardTitle} />

  </div>

    {/* ── Row 1: Loans by status + Delinquency aging ───────────────── */}
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:16 }}>

      {/* Accounts by status */}
      <Card style={{ padding:16 }}>
        <div style={cardTitle}>Accounts by Status</div>
        {accountsByStatus.length === 0
          ? <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>No accounts.</div>
          : accountsByStatus.map(s => {
              const pct = accountsByStatusShown ? Math.round(s.count / accountsByStatusShown * 100) : 0;
              return (
                <div key={s.status} style={{ marginBottom:8 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3 }}>
                    <span style={{ color:"var(--color-text-secondary)" }}>{s.status}</span>
                    <span style={{ fontWeight:500 }}>{s.count}</span>
                  </div>
                  <div style={{ height:6, background:"var(--color-background-secondary)", borderRadius:3 }}>
                    <div style={{ height:6, width:Math.min(100, pct) + "%", background:statusColor(s.status), borderRadius:3 }} />
                  </div>
                </div>
              );
            })}
      </Card>

      {/* Loans by status */}
      <Card style={{ padding:16 }}>
        <div style={cardTitle}>Loans by Status</div>
        {loansByStatusShown.map(s => {
          const pct = loansByStatusTotal ? Math.round(s.Count / loansByStatusTotal * 100) : 0;
          const barColor = statusColor(s.loanstatus);
          return (
            <div key={s.loanstatus} style={{ marginBottom:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3 }}>
                <span style={{ color:"var(--color-text-secondary)" }}>{s.loanstatus}</span>
                <span style={{ fontWeight:500 }}>{s.Count}</span>
              </div>
              <div style={{ height:6, background:"var(--color-background-secondary)", borderRadius:3 }}>
                <div style={{ height:6, width:Math.min(100, pct) + "%", background:barColor, borderRadius:3 }} />
              </div>
            </div>
          );
        })}
      </Card>

      {/* ── Delinquency aging ───────────────────────────────────────────────
          This card WAS a picture with no way through it, and the way through
          was a button two rows down labelled "Overdue and Upcoming Payments".
          The picture and the report are about the same thing, so the card now
          carries the report's two figures and IS the link. */}
      <PageLink page="overdue-payments" aria-label={totalDelinquent === 0
        ? "Overdue and Upcoming Payments report — nothing delinquent"
        : `Overdue and Upcoming Payments report — ${totalDelinquent} delinquent loan${totalDelinquent === 1 ? "" : "s"}, ${overduePayments.length} overdue payment${overduePayments.length === 1 ? "" : "s"}`}>
      <Card style={{ padding:16, transition:"box-shadow .12s, transform .12s" }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.14)";
                                 e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "";
                                 e.currentTarget.style.transform = ""; }}>
        <div style={cardTitle}>Delinquency Aging</div>
        <div style={{ fontSize:11, color:"var(--color-text-secondary)", marginBottom:12 }}>
          {totalDelinquent} loan{totalDelinquent !== 1 ? "s" : ""} with overdue payments
          {overdueAccounts > 0 ? ` across ${overdueAccounts} account${overdueAccounts === 1 ? "" : "s"}` : ""}
        </div>
        {totalDelinquent === 0
          ? <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
              No delinquent loans.{" "}
              <span style={{ color:B.cyanDark, fontWeight:600 }}>See what is due &rarr;</span>
            </div>
          : <>
              <div style={{ display:"flex", height:12, borderRadius:6, overflow:"hidden", marginBottom:16, background:"var(--color-background-secondary)" }}>
                {agingBuckets.map(b => b.value > 0 && (
                  <div key={b.label} style={{ width:(b.value/totalDelinquent*100)+"%", background:b.color, transition:"width 0.3s ease" }} title={b.label+": "+b.value} />
                ))}
              </div>
              {/* ONE ROW, not two. This card is the tallest in its row of
                  three, and a grid row is as tall as its tallest card — so the
                  second line of buckets was costing vertical space on two cards
                  that had nothing to put in it.
                  minmax(0, 1fr) rather than 1fr: a grid track's default minimum
                  is its content, so a long label would push the track wider
                  than its share and overflow the card instead of wrapping. */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4, minmax(0, 1fr))", gap:8 }}>
                {agingBuckets.map(b => (
                  <div key={b.label} style={{ display:"flex", alignItems:"flex-start", gap:6, minWidth:0 }}>
                    <div style={{ width:10, height:10, borderRadius:2, background:b.color, flexShrink:0, marginTop:2 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:11, color:"var(--color-text-secondary)", whiteSpace:"nowrap",
                                    overflow:"hidden", textOverflow:"ellipsis" }} title={b.label}>{b.label}</div>
                      <div style={{ fontSize:16, fontWeight:500, lineHeight:1.2, whiteSpace:"nowrap" }}>
                        {b.value || 0}
                        {totalDelinquent > 0 &&
                          <span style={{ fontSize:11, fontWeight:400, color:"var(--color-text-secondary)", marginLeft:4 }}>
                            ({Math.round((b.value||0)/totalDelinquent*100)}%)
                          </span>
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ── the report's own two numbers ──────────────────────────
                  LOANS above, PAYMENTS here, and both said out loud. One loan
                  three months behind is one loan and three payments, so a
                  reader who assumes these two counts describe the same thing
                  will conclude something false. The dollar figure is the
                  SHORTFALL — scheduled less received — because a payment half
                  made is half outstanding, not fully. */}
              <div style={{ borderTop:"1px solid var(--color-border-tertiary, #e6e9eb)",
                            marginTop:14, paddingTop:12 }}>
                <div style={reportRow}>
                  {/* The account count lives on the line at the top of the
                      card. Hung off this label it made the row wrap, leaving
                      the figure on one line and the word "accounts" alone on
                      the next. */}
                  <span style={{ color:"var(--color-text-secondary)" }}>Overdue payments</span>
                  <span style={{ fontWeight:600 }}>
                    {overduePayments.length}
                    <span style={{ color:AGE_BANDS[2].color, marginLeft:8 }}>
                      {dashMoney(overdueAmount)}</span>
                  </span>
                </div>
                <div style={reportRow}>
                  <span style={{ color:"var(--color-text-secondary)" }}>Due in the next 30 days</span>
                  <span style={{ fontWeight:600 }}>
                    {(up && up.cnt) || 0}
                    <span style={{ color:"var(--color-text-secondary)", marginLeft:8 }}>
                      {dashMoney((up && up.amount) || 0)}</span>
                  </span>
                </div>
                <div style={{ fontSize:12, fontWeight:600, color:B.cyanDark, marginTop:8 }}>
                  Open the report &rarr;
                </div>
              </div>
            </>
        }
      </Card>
      </PageLink>
    </div>

{/* The to-do cards live on the Action Center; its sidebar badge says
    whether anything is waiting there. */}

   {/* ── Portfolio by State ──────────────────────────────────────────────
       Same rows as the table below, joined to the account's state, so the
       map and the table cannot disagree.

       ABOVE the account table on purpose: the map is the summary and the
       table is the detail behind it, so the shape comes first and the
       line-by-line follows. */}
    <PortfolioHeatMap rows={activePortfolio} accounts={accounts} />

   {/* ── Portfolio by Account (grouped by status, Paid Off excluded) ── */}
    <Card style={{ padding:16 }}>
      <div style={cardTitle}>Portfolio by Account</div>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr>
            <ThSort col="companyname">Account</ThSort>
            <ThSort col="LoanCount" right>Loans</ThSort>
            <ThSort col="accountstatus">Status</ThSort>
            <ThSort col="Commitment" right>Commitment</ThSort>
            <ThSort col="Collateral" right>Collateral</ThSort>
            <ThSort col="TotalExposure" right>Loan Balance</ThSort>
            <ThSort col="BSExposure" right>BS Loan Balance</ThSort>
            <th style={{ textAlign:"left", padding:"8px 12px", fontSize:12, fontWeight:500, color:"var(--color-text-secondary)", borderBottom:"0.5px solid var(--color-border-tertiary)", background:"var(--color-background-secondary)", whiteSpace:"nowrap" }}>Alert</th>
          </tr>
        </thead>
        <tbody>
          {portfolioGroups.map(group => {
            const sub = portfolioTotalsFor(group.rows);
            return [
              <tr key={"hdr-"+group.status}>
                <td colSpan={8} style={{ padding:"8px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark, background:B.cyanLight }}>
                  <Badge s={group.status} />
                  <span style={{ marginLeft:8, color:B.cyanDark, fontSize:11 }}>{sub.count} account{sub.count!==1?"s":""}</span>
                </td>
              </tr>,
              ...group.rows.map(a => (
                <tr key={a.id} style={{ cursor:"pointer" }}
                  {...rowNav("account", { accountid: a.id })}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <Td style={{ fontWeight:500 }}><PageLink page="account" params={{ accountid: a.id }}>{a.companyname}</PageLink></Td>
                  <td style={pfNumCell}>{a.LoanCount}</td>
                  <Td><Badge s={a.accountstatus} /></Td>
                  <td style={pfNumCell}>{fmt(a.Commitment || 0)}</td>
                  <td style={pfNumCell}>{fmt(a.Collateral || 0)}</td>
                  <td style={pfNumCell}>{fmt(a.TotalExposure || 0)}</td>
                  <td style={pfNumCell}>{fmt(a.BSExposure || 0)}</td>
                  <Td>
                    {alertsFor(a).length === 0 ? null :
                      <div style={{ display:"flex", flexWrap:"wrap", gap:4, maxWidth:220 }}>
                        {alertsFor(a).map(al => <span key={al} style={alertChip(al)}>{al}</span>)}
                      </div>}
                  </Td>
                </tr>
              )),
              <tr key={"sub-"+group.status} style={{ background:"var(--color-background-secondary)", borderTop:"1.5px solid "+B.cyanLight }}>
                <td style={{ padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark }}>{group.status} Subtotal ({sub.count})</td>
                <td style={pfSubNum}>{sub.loans}</td>
                <td />
                <td style={pfSubNum}>{fmt(sub.commitment)}</td>
                <td style={pfSubNum}>{fmt(sub.collateral)}</td>
                <td style={pfSubNum}>{fmt(sub.exposure)}</td>
                <td style={pfSubNum}>{fmt(sub.bs)}</td>
                <td />
              </tr>,
            ];
          })}
        </tbody>
        <tfoot>
          <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan }}>
            <td style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark }}>Grand Total ({portfolioGrand.count} accounts)</td>
            <td style={pfGrandNum}>{portfolioGrand.loans}</td>
            <td />
            <td style={pfGrandNum}>{fmt(portfolioGrand.commitment)}</td>
            <td style={pfGrandNum}>{fmt(portfolioGrand.collateral)}</td>
            <td style={pfGrandNum}>{fmt(portfolioGrand.exposure)}</td>
            <td style={pfGrandNum}>{fmt(portfolioGrand.bs)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </Card>





  </>;
};

// ── Unposted sweeps ────────────────────────────────────────────────────────
//
// This replaced a five-column table of every unmapped sweep. The table was
// honest and nobody read it: a dashboard tile that lists rows is asking you to
// do the work in the wrong place, and the work happens on Pmt Posting. What a
// dashboard owes you is the SIZE of the pile and a way through to it.
//
// So: one number, what it is worth, and the one split that changes what you do
// about it. A sweep WITH a loan reference is waiting to be posted. One WITHOUT
// has nothing to post against until somebody matches it — a different job, and
// the one that stalls if nobody looks. Both segments are labelled, so the bar
// is never the only thing carrying the distinction.
//
// COLOURS ARE NOT EYEBALLED. #0e93b0 / #c84e1a were run through the palette
// validator against a white surface: both inside the lightness band, both over
// 3:1 against the surface, and the pair separates at ΔE 19.3 under deuteranopia
// (floor 8) and 27.7 for normal vision (floor 15). The brand cyan #12b5db and
// the amber #ffb20d that the old tiles used FAIL that check — 1.76:1 against
// white — which is why these are a step darker than the buttons beside them.
const SWEEP_MAPPED   = "#0e93b0";
const SWEEP_UNMAPPED = "#c84e1a";

// shared/format's `fmt` carries cents, which is right in a column you are going
// to reconcile and wrong beside a 38px headline — "$1,284,300.47" is four
// characters of noise on a figure nobody is going to tie out from a dashboard.
// Whole dollars here; the exact amounts are one click away on Pmt Posting.
const sweepMoney = n => "$" + Math.round(Number(n) || 0).toLocaleString("en-US");

export const UnpostedSweeps = ({ count, total, unmapped, mapped, title }) => {
  const none = count === 0;

  // Below about 4% a segment is a smudge, so it is floored to something you can
  // see — and the floor is TAKEN FROM THE OTHER SEGMENT, not added on top. The
  // first version added it, so 79 matched + 1 unmapped came to 101.75% and the
  // bar ran past its own container. The labels below carry the true counts; the
  // floor only keeps a real, small quantity from rendering as nothing.
  const MIN = 4;
  let [growA, growB] = [0, 0];
  if (count) {
    growA = (mapped / count) * 100;
    growB = 100 - growA;
    if (mapped   > 0 && growA < MIN) { growA = MIN; growB = 100 - MIN; }
    if (unmapped > 0 && growB < MIN) { growB = MIN; growA = 100 - MIN; }
    if (mapped   === 0) { growA = 0;   growB = 100; }
    if (unmapped === 0) { growA = 100; growB = 0;   }
  }

  return (
    <PageLink page="all-wftransactions" style={{ display:"block", height:"100%" }} aria-label={none
      ? "Pmt Posting — every sweep is posted"
      : `Pmt Posting — ${count} unposted sweep${count === 1 ? "" : "s"}, ${unmapped} with no loan reference`}>
      {/* height:100% so this tile matches the others in its row rather than
          setting the row's height on its own. */}
      <Card style={{ padding:16, height:"100%", boxSizing:"border-box",
                     transition:"box-shadow .12s, transform .12s" }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.14)";
                                 e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "";
                                 e.currentTarget.style.transform = ""; }}>
        <div style={title}>Unposted sweeps</div>

        {none ? (
          <>
            <div style={{ fontSize:13, color:"var(--color-text-secondary)", marginBottom:10 }}>
              Everything is posted.
            </div>
            {/* Still a way through. The page is worth opening even on a clear
                day — you may be looking for something already posted. */}
            <div style={{ fontSize:12, fontWeight:600, color:SWEEP_MAPPED }}>
              Open Pmt Posting &rarr;
            </div>
          </>
        ) : (
          <>
            {/* Proportional figures, not tabular — tabular-nums gives every digit
                the width of a zero, which reads loose at display size. Tabular
                belongs in columns that have to line up, not on a lone number. */}
            {/* flexWrap so the total drops under the count rather than pushing
                out of the card. Four digits next to a nine-figure total is the
                case that needs it, and at 220px the column has 186px to give. */}
            <div style={{ display:"flex", alignItems:"baseline", gap:10, marginBottom:2,
                          flexWrap:"wrap" }}>
              <span style={{ fontSize:38, fontWeight:600, lineHeight:1,
                             color:"var(--color-text-primary)" }}>{count}</span>
              <span style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
                {sweepMoney(total)}
              </span>
            </div>

            {/* Two segments, 2px of surface between them so they read as two
                quantities rather than one bar that changes colour.

                flex-grow, NOT width percentages: grow divides what is left AFTER
                the 2px gap, so the bar fits its container exactly at any width
                and the gap never has to be subtracted from a percentage.

                The rounding is on the CONTAINER with overflow hidden, so the
                outer ends are round and the two inner ends stay square against
                the gap — whichever segments happen to be present. Rounding each
                child instead meant a lone segment kept a square end, which read
                as a bar that had been cut off. */}
            <div style={{ display:"flex", gap:2, height:10, marginTop:12, marginBottom:10,
                          borderRadius:5, overflow:"hidden" }}>
              {mapped > 0 && (
                <div style={{ flex:`${growA} 0 0`, background:SWEEP_MAPPED }} />
              )}
              {unmapped > 0 && (
                <div style={{ flex:`${growB} 0 0`, background:SWEEP_UNMAPPED }} />
              )}
            </div>

            <div style={{ display:"flex", flexDirection:"column", gap:5, marginBottom:10 }}>
              <SweepKey color={SWEEP_MAPPED} n={mapped} label="matched to a loan" />
              <SweepKey color={SWEEP_UNMAPPED} n={unmapped} label="no loan reference yet" />
            </div>

            <div style={{ fontSize:12, fontWeight:600, color:SWEEP_MAPPED }}>
              Post them &rarr;
            </div>
          </>
        )}
      </Card>
    </PageLink>
  );
};

// The count stays in text ink and the swatch beside it carries the identity —
// a coloured number would make the figure itself decoration.
const SweepKey = ({ color, n, label }) => (
  <div style={{ display:"flex", alignItems:"center", gap:7, fontSize:12 }}>
    <span style={{ width:8, height:8, borderRadius:2, background:color, flex:"0 0 auto" }} />
    <span style={{ fontWeight:600, color:"var(--color-text-primary)" }}>{n}</span>
    <span style={{ color:"var(--color-text-secondary)" }}>{label}</span>
  </div>
);

// ── The open-items tiles ───────────────────────────────────────────────────
//
// Unfunded draws and un-set-up ACH collections used to be TABLES on this row,
// and a table on a dashboard has the wrong growth behaviour: the card is short
// on a quiet day and pushes everything below it off the screen on a busy one,
// which is exactly backwards from how much attention each of those days
// deserves. These are the same two piles rendered the way the sweeps and
// participants tiles render theirs — a fixed-height answer to "is there
// anything here, how much, and how late is it" — with the rows themselves one
// click away.
//
// WHAT WAS WORTH TAKING FROM THE COVENANTS CARD: a count alone does not tell
// you whether to worry. Six draws is a normal week; six draws whose fund date
// passed a fortnight ago is not. So each tile carries the same three things
// that card carries — the size of the pile, its split by lateness, and how bad
// the worst one is — and the worst figure wears the colour of its aging band,
// so "darker means later" reads the same here as it does there.

// Days since a date, measured at LOCAL midnight. Positive means it has passed.
// Parsed field-by-field rather than through Date(string): an ISO date alone is
// parsed as UTC, which lands on the previous day for anyone west of Greenwich
// and makes a draw due today read as one day late.
export const daysSince = (v) => {
  if (!v) return null;
  const p = String(v).slice(0, 10).split("-");
  if (p.length !== 3) return null;
  const d = new Date(+p[0], +p[1] - 1, +p[2]);
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((today - d) / 86400000);
};

// Two or more quantities on one bar.
//
// flex-grow, NOT width percentages: grow divides what is left AFTER the 2px
// gaps, so the bar fits its container exactly at any width. The rounding is on
// the CONTAINER with overflow hidden, so the outer ends are round and the inner
// joins stay square against the gaps — whichever segments happen to be present.
//
// Below about 4% a segment is a smudge, so it is floored to something visible —
// and the floor is TAKEN FROM the larger segments rather than added on top, or
// the bar would sum past 100 and run out of its own container. The legend
// underneath always carries the true counts; the floor only keeps a real, small
// quantity from rendering as nothing.
const BAR_MIN_PCT = 4;
const SplitBar = ({ segments, height = 10 }) => {
  const live = (segments || []).filter(s => (Number(s.value) || 0) > 0);
  if (!live.length) return null;
  const sum = live.reduce((a, s) => a + Number(s.value), 0);
  let pct = live.map(s => Number(s.value) / sum * 100);
  const small = pct.map(v => v < BAR_MIN_PCT);
  if (small.some(Boolean) && !small.every(Boolean)) {
    const owed = pct.reduce((a, v, i) => a + (small[i] ? BAR_MIN_PCT - v : 0), 0);
    const big  = pct.reduce((a, v, i) => a + (small[i] ? 0 : v), 0);
    pct = pct.map((v, i) => small[i] ? BAR_MIN_PCT : v - owed * (v / big));
  }
  return (
    <div style={{ display:"flex", gap:2, height, borderRadius:height / 2, overflow:"hidden" }}>
      {live.map((s, i) => (
        <div key={s.key || i} style={{ flex:`${pct[i]} 0 0`, background:s.color }}
             title={s.title} />
      ))}
    </div>
  );
};

// The card body every tile on this row shares: the hover lift, the link target,
// and the title. Written once so four tiles cannot drift into four slightly
// different cards.
const TileCard = ({ page, ariaLabel, title, heading, children }) => (
  <PageLink page={page} aria-label={ariaLabel} style={{ display:"block", height:"100%" }}>
    <Card style={{ padding:16, height:"100%", boxSizing:"border-box",
                   transition:"box-shadow .12s, transform .12s" }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.14)";
                               e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = "";
                               e.currentTarget.style.transform = ""; }}>
      <div style={title}>{heading}</div>
      {children}
    </Card>
  </PageLink>
);

const tileCta  = { fontSize:12, fontWeight:600, color:B.cyanDark };
const tileCalm = { fontSize:13, color:"var(--color-text-secondary)", marginBottom:10 };

// One shape, two piles. Both answer: how many are waiting, what are they worth,
// how many of them are already past the date they were meant to happen on, and
// how far past is the worst.
export const OpenItemsTile = ({ page, heading, title, rows, amountOf, dateOf,
                        lateLabel, aheadLabel, cta, calm, unit }) => {
  const LATE = AGE_BANDS[2].color, AHEAD = "#0e93b0";
  const list = rows || [];
  const n = list.length;

  if (!n) {
    return (
      <TileCard page={page} title={title} heading={heading}
                ariaLabel={`${heading} — nothing waiting`}>
        <div style={tileCalm}>{calm}</div>
        <div style={tileCta}>{cta} &rarr;</div>
      </TileCard>
    );
  }

  const aged  = list.map(r => daysSince(dateOf(r)));
  // A row with no date at all counts as waiting rather than scheduled — an item
  // nobody has dated is not a thing that is safely in the future.
  const late  = aged.filter(d => d === null || d >= 0).length;
  const ahead = n - late;
  const worst = aged.reduce((m, d) => (d !== null && d > m ? d : m), 0);
  const next  = aged.reduce((m, d) => (d !== null && d < 0 && d > m ? d : m), -Infinity);
  const money = list.reduce((a, r) => a + (Number(amountOf(r)) || 0), 0);

  return (
    <TileCard page={page} title={title} heading={heading}
              ariaLabel={`${heading} — ${n} ${unit}${n === 1 ? "" : "s"} waiting, ${late} past due`}>
      <div style={{ display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap" }}>
        <span style={{ fontSize:38, fontWeight:600, lineHeight:1,
                       color:"var(--color-text-primary)" }}>{n}</span>
        <span style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
          {headlineMoney(money)}
        </span>
      </div>

      {/* The covenants card's best line, borrowed: how bad is the worst one.
          Six draws is a normal week; six whose date passed a fortnight ago is
          not, and the count alone cannot tell those apart. */}
      <div style={{ fontSize:12, marginTop:5,
                    color: late > 0 ? bandFor(worst).color : "var(--color-text-secondary)",
                    fontWeight: late > 0 ? 600 : 400 }}>
        {late > 0
          ? (worst > 0 ? `oldest is ${worst} day${worst === 1 ? "" : "s"} past due`
                       : "due today")
          : (next > -Infinity ? `next one in ${-next} day${next === -1 ? "" : "s"}`
                              : "none dated yet")}
      </div>

      <div style={{ marginTop:12, marginBottom:10 }}>
        <SplitBar segments={[
          { key:"late",  value:late,  color:LATE,  title:`${lateLabel}: ${late}` },
          { key:"ahead", value:ahead, color:AHEAD, title:`${aheadLabel}: ${ahead}` },
        ]} />
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:5, marginBottom:10 }}>
        <SweepKey color={LATE}  n={late}  label={lateLabel} />
        <SweepKey color={AHEAD} n={ahead} label={aheadLabel} />
      </div>

      <div style={tileCta}>{cta} &rarr;</div>
    </TileCard>
  );
};

// ── Funding in Process ─────────────────────────────────────────────────────
//
// Deals on their way to funding: how many, what they are worth, and how many
// have nothing entered against them yet.
//
// ACCOUNTS ARE THE HEADLINE, FACILITIES ARE THE DETAIL. One borrower with two
// facilities is one deal in the pipeline, not two, and a count that said two
// would overstate the pipeline every time a deal was structured in tranches.
//
// The split is terms entered versus not. An account sitting in funding with no
// term on it is the only thing on this tile anybody can act on from here —
// everything else is just the size of the queue.
const FundingInProcess = ({ accounts, facilities, withTerms, noTerms,
                            commitment, funding, title }) => {
  const OPEN = "#0e93b0", BLANK = AGE_BANDS[2].color;

  if (!accounts) {
    return (
      <TileCard page="accounts" title={title} heading="Funding in Process"
                ariaLabel="Funding in process — nothing in the pipeline">
        <div style={tileCalm}>No accounts in funding.</div>
        <div style={tileCta}>Open accounts &rarr;</div>
      </TileCard>
    );
  }

  return (
    <TileCard page="accounts" title={title} heading="Funding in Process"
              ariaLabel={`Funding in process — ${accounts} account${accounts === 1 ? "" : "s"}, `
                       + `${noTerms} with no terms entered`}>
      <div style={{ display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap" }}>
        <span style={{ fontSize:38, fontWeight:600, lineHeight:1,
                       color:"var(--color-text-primary)" }}>{accounts}</span>
        <span style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
          {headlineMoney(commitment)} committed
        </span>
      </div>

      {/* Facilities only when it differs from the account count — otherwise the
          line restates the big number in smaller type. */}
      <div style={{ fontSize:12, marginTop:5, color:"var(--color-text-secondary)" }}>
        {facilities > accounts
          ? `${facilities} facilities · ${headlineMoney(funding)} to fund`
          : `${headlineMoney(funding)} to fund`}
      </div>

      <div style={{ marginTop:12, marginBottom:10 }}>
        <SplitBar segments={[
          { key:"termed", value:withTerms, color:OPEN,  title:`terms entered: ${withTerms}` },
          { key:"blank",  value:noTerms,   color:BLANK, title:`no terms yet: ${noTerms}` },
        ]} />
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:5, marginBottom:10 }}>
        <SweepKey color={OPEN}  n={withTerms} label="terms entered" />
        <SweepKey color={BLANK} n={noTerms}   label="no terms entered yet" />
      </div>

      <div style={tileCta}>Open funding worklist &rarr;</div>
    </TileCard>
  );
};

// ── Overdue covenants ──────────────────────────────────────────────────────
//
// Three questions about one pile, side by side: how big is it, how old is it,
// and what is actually stuck. A count alone does not tell you whether to worry —
// forty covenants a week late is a busy month, and four of them nine months late
// is a different conversation.
//
// WHAT "OVERDUE" MEANS IS NOT DECIDED HERE. It is decided once in
// api/shared/covenantOverdue.js, because three screens used to answer that
// question differently. This card renders what the server counted.
//
// THE AGING RAMP IS ORDERED AND VALIDATED. Light amber to deep red, more-late
// is darker, so the colour carries the same thing the order does. The four steps
// pass the ordinal checks against a white surface: lightness monotone, every
// adjacent gap over 0.06, hue spread 37° (one hue, not a rainbow), and the light
// end at 2.70:1. The ramp on the Delinquency Aging card above is the same family
// but starts at #ffb20d, which is 1.81:1 against white — legible as a wide bar,
// not as an 8px swatch, which is why this one starts darker.
const AGE_BANDS = [
  { key:"b_1_30",   label:"1–30 days",  color:"#d98a00" },
  { key:"b_31_60",  label:"31–60 days", color:"#c4611a" },
  { key:"b_61_90",  label:"61–90 days", color:"#ad3f1c" },
  { key:"b_90plus", label:"over 90",    color:"#8a2d1c" },
];

// Which band a number of days falls in. Used to colour a category by its OLDEST
// covenant, so the legend above teaches the scale and the category list reuses
// it rather than introducing a second one.
const bandFor = d => d > 90 ? AGE_BANDS[3] : d > 60 ? AGE_BANDS[2]
                   : d > 30 ? AGE_BANDS[1] : AGE_BANDS[0];

const COV_PAGE = "all-covenant-calendar-visual";

// Past eight the rows stop being a list and start being a table nobody reads, so
// the tail folds into one row. The count in "Other" is still the true count.
const CATEGORY_ROWS = 6;

// ── COMPACT ──
// The card now shares its row with Tax Monitoring, so it has roughly half the
// width it was designed for. Three things give way, in the order that costs the
// least: the four panels become a 2x2 block instead of one row, the headline
// drops from 38 to 30 (at 38 a three-digit count and the word beside it stop
// fitting in a 160px column), and the two lists get shorter — four categories
// and three names instead of six and five. The TOTALS are untouched: the tail
// line still accounts for every category and the count above still counts every
// covenant, so nothing disappears, it just stops being enumerated.
//
// marginBottom comes off in compact mode because the grid wrapper owns the gap;
// leaving it on would have put 16px of air under one card and not the other and
// broken the alignItems:stretch that keeps them the same height.
export const OverdueCovenants = ({ co, title, compact }) => {
  const d = co || {};
  const total = d.total || 0;
  // The headline is the RED count — more than a week late — because that is
  // what the sidebar bubble and the Action Center count. Everything overdue is
  // still in the aging, the categories and the list below.
  const red = d.red || 0;
  const catRows  = compact ? 4 : CATEGORY_ROWS;
  const worstCap = compact ? 3 : 5;
  const shell    = { padding:16, marginBottom: compact ? 0 : 16, height:"100%", boxSizing:"border-box" };

  if (!total) {
    return (
      <Card style={shell}>
        <div style={title}>Overdue covenants</div>
        <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
          {/* Cyan, not amber. The amber ramp means "late" everywhere else on
              this card, and painting the clear-day link with it makes a good
              result look like a warning. */}
          Nothing overdue. <PageLink page={COV_PAGE} params={{ mode: "table" }}
            style={{ color:B.cyanDark, fontWeight:600 }}>Open the calendar &rarr;</PageLink>
        </div>
      </Card>
    );
  }

  const cats = d.byCategory || [];
  const head = cats.slice(0, catRows);
  const tail = cats.slice(catRows);
  const tailCount = tail.reduce((s, c) => s + (c.cnt || 0), 0);
  // The bars are scaled to the LARGEST CATEGORY, not to the total. Against the
  // total every bar is short and they all look alike; against the leader the
  // comparison the reader is actually making — this one against that one — is
  // the one the lengths show.
  const catMax = Math.max(1, ...cats.map(c => c.cnt || 0));

  return (
    <Card style={shell}>
      <div style={title}>Overdue covenants</div>
      <div style={{ display:"grid", gap: compact ? 16 : 24,
                    gridTemplateColumns: compact ? "repeat(2, minmax(0, 1fr))"
                                                 : "repeat(auto-fit, minmax(210px, 1fr))" }}>

        {/* ── the size of it ── */}
        <div>
          <div style={{ display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap" }}>
            <span style={{ fontSize: compact ? 30 : 38, fontWeight:600, lineHeight:1,
                           color: red ? "#B42318" : "var(--color-text-primary)" }}>{red}</span>
            <span style={{ fontSize:13, color:"var(--color-text-secondary)" }}>more than a week late</span>
          </div>
          <div style={{ fontSize:13, color:"var(--color-text-secondary)", marginTop:6 }}>
            {red ? <>across {d.redaccounts || 0} account{d.redaccounts === 1 ? "" : "s"} · </> : null}
            {total} overdue in all
          </div>
          <div style={{ fontSize:13, color:bandFor(d.worstdays || 0).color,
                        fontWeight:600, marginTop:4 }}>
            oldest is {d.worstdays || 0} day{d.worstdays === 1 ? "" : "s"} late
          </div>
          <PageLink page={COV_PAGE} params={{ mode: "table", filter: "overdue" }} style={{ display:"inline-block", marginTop:12,
                    fontSize:12, fontWeight:600, color:B.cyanDark }}>
            Open the calendar &rarr;
          </PageLink>
        </div>

        {/* ── how late ── */}
        <div>
          <div style={subhead}>How late</div>
          {/* Rounding on the container with overflow hidden: the outer ends of
              the bar are round and the joins between bands stay square against
              the 2px gaps, whichever bands happen to be empty. */}
          <div style={{ display:"flex", gap:2, height:10, marginBottom:10,
                        borderRadius:5, overflow:"hidden" }}>
            {AGE_BANDS.map(b => (d[b.key] || 0) > 0 && (
              <div key={b.key} style={{ flex:`${d[b.key]} 0 0`, background:b.color }} />
            ))}
          </div>
          {AGE_BANDS.map(b => (
            <div key={b.key} style={{ display:"flex", alignItems:"center", gap:7,
                                      fontSize:12, marginBottom:5 }}>
              <span style={{ width:8, height:8, borderRadius:2, background:b.color,
                             flex:"0 0 auto" }} />
              <span style={{ fontWeight:600, color:"var(--color-text-primary)",
                             minWidth:26 }}>{d[b.key] || 0}</span>
              <span style={{ color:"var(--color-text-secondary)" }}>{b.label}</span>
            </div>
          ))}
        </div>

        {/* ── by category ── */}
        <div>
          <div style={subhead}>By category</div>
          {head.map(c => (
            <div key={c.category} style={{ marginBottom:7 }}>
              <div style={{ display:"flex", justifyContent:"space-between",
                            fontSize:12, marginBottom:3, gap:8 }}>
                <span style={{ color:"var(--color-text-primary)", overflow:"hidden",
                               textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                      title={`${c.category} — oldest ${c.worstdays} days late`}>
                  {c.category}
                </span>
                <span style={{ fontWeight:600, flex:"0 0 auto" }}>{c.cnt}</span>
              </div>
              {/* Length is the count; the SHADE is the oldest one in that
                  category, on the same ramp as the legend beside it. A category
                  with two nine-month-old covenants should not read as calm just
                  because it is short. */}
              <div style={{ height:6, borderRadius:3,
                            background:"var(--color-background-secondary)" }}>
                <div style={{ height:6, borderRadius:3,
                              width:`${Math.max(4, ((c.cnt || 0) / catMax) * 100)}%`,
                              background:bandFor(c.worstdays || 0).color }} />
              </div>
            </div>
          ))}
          {tail.length > 0 && (
            <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginTop:6 }}>
              + {tailCount} in {tail.length} other categor{tail.length === 1 ? "y" : "ies"}
            </div>
          )}
        </div>

        {/* ── what is actually stuck ── */}
        <div>
          <div style={subhead}>Longest outstanding</div>
          {(d.worst || []).slice(0, worstCap).map(w => (
            <div key={w.id} style={{ marginBottom:8, fontSize:12, lineHeight:1.35 }}>
              <div style={{ display:"flex", justifyContent:"space-between", gap:8 }}>
                <span style={{ fontWeight:600, color:"var(--color-text-primary)",
                               overflow:"hidden", textOverflow:"ellipsis",
                               whiteSpace:"nowrap" }}>{w.companyname}</span>
                <span style={{ fontWeight:600, flex:"0 0 auto",
                               color:bandFor(w.daysoverdue || 0).color }}>
                  {w.daysoverdue}d
                </span>
              </div>
              <div style={{ color:"var(--color-text-secondary)", overflow:"hidden",
                            textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                   title={`${w.covenantname} · ${w.category} · ${w.status || "no status"}`}>
                {w.covenantname}
              </div>
            </div>
          ))}
        </div>

      </div>
    </Card>
  );
};

// ── UCC monitoring ─────────────────────────────────────────────────────────
//
// Between covenants and tax, and the same shape as both: how big is the pile,
// what kind of thing is in it, and is the live book actually covered.
//
// LIVE BOOK ONLY. A paid-off borrower's filing lapsing is the expected end of
// it, not a problem, so findings and coverage count accounts in Accrual and
// Non Accrual only. The UCC Filings page still shows all.
//
// The findings are the register's own flags (vUCCFilingStatus), the same ones
// the UCC Filings worklist and Needs column read, so the two cannot disagree.
const UCC_PAGE = "ucc-register";
// Funded and not paid off. Funding in Progress (never funded yet), Paid Off and
// the rest are left out of the UCC card — no lien of ours to watch.
export const UCC_LIVE = ["Accrual", "Non Accrual"];
export const UCC_KINDS = [
  { key:"lapsed",   label:"Lapsed",                    color:"#8a2d1c", test: v => v.status === "LAPSED" },
  { key:"office",   label:"Filed in the wrong state",  color:"#ad3f1c", test: v => !!v.jurisdiction_mismatch },
  { key:"window",   label:"Continuation window open",  color:"#c4611a", test: v => !!v.in_continuation_window },
  { key:"name",     label:"Debtor name mismatch",      color:"#d98a00", test: v => !!v.name_drift },
];

export const UccMonitoring = ({ ucc, accounts, title }) => {
  const shell = { padding:16, height:"100%", boxSizing:"border-box" };
  if (!ucc) {
    return (
      <Card style={shell}>
        <div style={title}>UCC Filings</div>
        <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
          Not reporting. The UCC register may not be set up yet.{" "}
          <PageLink page={UCC_PAGE} style={{ color:B.cyanDark, fontWeight:600 }}>Open UCC filings &rarr;</PageLink>
        </div>
      </Card>
    );
  }

  const live = (ucc.rows || []).filter(v => v.status !== "Terminated" && UCC_LIVE.includes(v.accountstatus));
  const flagged = live.filter(v => UCC_KINDS.some(k => k.test(v)));
  const total = flagged.length;
  const acctCount = new Set(flagged.map(v => v.accountid)).size;
  const kindCount = k => live.filter(k.test).length;
  const maxKind = Math.max(1, ...UCC_KINDS.map(kindCount));

  // The soonest lapse still ahead among live filings.
  const ahead = live.map(v => v.days_to_lapse).filter(n => typeof n === "number" && n >= 0);
  const soonest = ahead.length ? Math.min(...ahead) : null;

  // Coverage: live borrowers with no live (non-lapsed) filing in the register.
  const covered = new Set(live.filter(v => v.status !== "LAPSED").map(v => String(v.accountid)));
  const liveAccts = (accounts || []).filter(a => UCC_LIVE.includes(a.accountstatus));
  // A borrower whose UCC search finding was marked known — with a reason — on
  // UCC Searches is not raised here again; it shows as a quiet "with a reason" line.
  const reasons = new Map(((ucc.searches && ucc.searches.acknowledged) || []).map(x => [String(x.accountid), x.notes || []]));
  const noFiling = liveAccts.filter(a => !covered.has(String(a.id)));
  const uncovered = noFiling.filter(a => !reasons.has(String(a.id)));
  const excused = noFiling.filter(a => reasons.has(String(a.id)));
  const review = ucc.csc && ucc.csc.review ? ucc.csc.review : 0;
  // The headline is the sidebar badge on UCC Filings: filings with a problem,
  // funded borrowers with no active filing (no reason on record), and CSC
  // filings waiting for review. The three lines under it add up to it.
  const attention = total + uncovered.length + review;

  const coverage = (
    <div>
      <div style={subhead}>Coverage</div>
      {uncovered.length > 0 ? (
        <div style={{ fontSize:12.5, fontWeight:600, color:"#8a2d1c", marginBottom:6 }}
             title={uncovered.slice(0, 25).map(a => a.companyname).join("\n")}>
          {/* Opens UCC Filings on its No filing list, where each one can be filed or given a reason. */}
          <PageLink page={UCC_PAGE} params={{ mode: "nofiling" }} style={{ color:"#8a2d1c" }}>
            {uncovered.length} live borrower{uncovered.length === 1 ? "" : "s"} with no active filing &rarr;
          </PageLink>
        </div>
      ) : (
        <div style={{ fontSize:12.5, fontWeight:600, color:"#0F6E56", marginBottom:6 }}>{excused.length ? "No unexplained gaps" : "Every live borrower filed"}</div>
      )}
      {excused.length > 0 && (
        <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:3 }}
             title={excused.map(a => a.companyname + ((reasons.get(String(a.id)) || []).length ? " — " + reasons.get(String(a.id)).join("; ") : "")).join("\n")}>
          {excused.length} without a filing, reason recorded
        </div>
      )}
      <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:3 }}>
        {live.length} live filing{live.length === 1 ? "" : "s"} on {covered.size} borrower{covered.size === 1 ? "" : "s"}
      </div>
    </div>
  );

  const link = <PageLink page={UCC_PAGE} style={{ display:"inline-block", marginTop:12, fontSize:12,
                         fontWeight:600, color:B.cyanDark }}>Open UCC filings &rarr;</PageLink>;

  const partRow = (label, n, page, params) => (
    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"4px 0",
                  borderTop:"0.5px solid var(--color-border-tertiary)" }}>
      {page && n ? <PageLink page={page} params={params} style={{ color:B.cyanDark }}>{label}</PageLink> : <span>{label}</span>}
      <span style={{ fontWeight:600, fontVariantNumeric:"tabular-nums", color: n ? "#8a2d1c" : undefined }}>{n}</span>
    </div>);
  const parts = (
    <div style={{ marginTop:10 }}>
      {partRow("Filings with a problem", total, UCC_PAGE)}
      {partRow("Funded borrowers with no filing", uncovered.length, UCC_PAGE, { mode: "nofiling" })}
      {partRow("CSC filings to review", review, UCC_PAGE)}
    </div>);

  if (!attention) {
    return (
      <Card style={shell}>
        <div style={title}>UCC Filings</div>
        <div style={{ display:"grid", gap:16, gridTemplateColumns:"repeat(2, minmax(0, 1fr))" }}>
          <div>
            <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
              Nothing needs attention — no lapsed filings, open continuation windows or office mismatches on the live book.
            </div>
            {link}
          </div>
          {coverage}
        </div>
      </Card>
    );
  }

  return (
    <Card style={shell}>
      <div style={title}>UCC Filings</div>
      <div style={{ display:"grid", gap:16, gridTemplateColumns:"repeat(2, minmax(0, 1fr))" }}>
        <div>
          <div style={{ fontSize:30, fontWeight:600, lineHeight:1, color:"var(--color-text-primary)" }}>{attention}</div>
          <div style={{ fontSize:13, color:"var(--color-text-secondary)", marginTop:6 }}>
            need{attention === 1 ? "s" : ""} attention{total ? ` · ${total} filing${total === 1 ? "" : "s"} across ${acctCount} account${acctCount === 1 ? "" : "s"}` : ""}
          </div>
          {parts}
          {soonest != null && (
            <div style={{ fontSize:13, fontWeight:600, marginTop:4,
                          color: soonest <= 60 ? "#8a2d1c" : soonest <= 180 ? "#c4611a" : "#d98a00" }}>
              soonest lapse in {soonest} day{soonest === 1 ? "" : "s"}
            </div>
          )}
          {link}
        </div>
        <div>
          <div style={subhead}>What needs doing</div>
          {UCC_KINDS.map(k => {
            const n = kindCount(k);
            return (
              <div key={k.key} style={{ marginBottom:6 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:2 }}>
                  <span>{k.label}</span><span style={{ fontWeight:600, fontVariantNumeric:"tabular-nums" }}>{n}</span>
                </div>
                <div style={{ height:6, borderRadius:3, background:"var(--color-background-secondary)" }}>
                  {n > 0 && <div style={{ width:`${(n / maxKind) * 100}%`, height:"100%", borderRadius:3, background:k.color }} />}
                </div>
              </div>
            );
          })}
          <div style={{ marginTop:10 }}>{coverage}</div>
        </div>
      </div>
    </Card>
  );
};

// ── Tax monitoring ─────────────────────────────────────────────────────────
//
// The twin of the covenants card beside it, and deliberately the same shape:
// how big is the pile, how serious is it, what KIND of thing is in it, and —
// the one that has no covenant equivalent — is anybody actually watching.
//
// ── WHY COVERAGE IS ON A DASHBOARD CARD AT ALL ──────────────────────────────
//
// Federal tax monitoring fails SILENTLY. If a borrower's Form 8821 lapses, or
// they were never enrolled with Tax Guard, nothing errors — the findings simply
// stop arriving, and a borrower nobody is watching looks exactly like a borrower
// with nothing to find. A card that showed only the findings would therefore
// report its best number on its worst day. So the fourth panel says how many
// entities a finding could not reach us from, and the empty state shows that
// panel too: "nothing needs attention" is only good news when the coverage line
// beside it says somebody was looking.
//
// Numbers come from getTaxMonitoring — the same payload the Tax Monitoring page
// renders — so the two cannot disagree.
const TAX_PAGE = "tax-monitoring";

// The dashboard's one amber-to-red ramp, borrowed rather than re-picked. Three
// steps instead of four because the worklist has three severities; they are
// AGE_BANDS[3], [1] and [0], which keeps the "darker is worse" reading identical
// to the two cards above and avoids introducing a fourth palette to the page.
const TAX_SEVERITY = [
  { sev:1, label:"Dated deadline",   color:"#8a2d1c" },
  { sev:2, label:"Open exposure",    color:"#c4611a" },
  { sev:3, label:"Balance reported", color:"#d98a00" },
];

export const TaxMonitoring = ({ tax, title }) => {
  const shell = { padding:16, height:"100%", boxSizing:"border-box" };

  // ── NOT SILENTLY ABSENT ──
  // getTaxMonitoring answers 503 until the sql/ scripts have been run, and the
  // load() catch turns that into null. Rendering nothing would leave a dashboard
  // that looks complete and is missing a whole category of risk — the exact
  // failure this card exists to prevent, one level up. So it says so.
  if (!tax) {
    return (
      <Card style={shell}>
        <div style={title}>Tax monitoring</div>
        <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
          Not reporting. The monitoring views may not be installed yet.{" "}
          <PageLink page={TAX_PAGE} style={{ color:B.cyanDark, fontWeight:600 }}>
            Open tax monitoring &rarr;
          </PageLink>
        </div>
      </Card>
    );
  }

  const worklist    = Array.isArray(tax.worklist) ? tax.worklist : [];
  const entities    = Array.isArray(tax.entities) ? tax.entities : [];
  const liabilities = Array.isArray(tax.liabilities) ? tax.liabilities : [];

  // Acknowledged findings are excluded here exactly as they are on the page —
  // an acknowledgement is how a finding leaves the list, and a dashboard that
  // kept counting them would make acknowledging pointless.
  const open     = worklist.filter(w => !w.acked_at);
  const total    = open.length;
  const accounts = new Set(open.map(w => w.accountid)).size;

  // Coverage. isUnmonitored is shared with the page's Coverage card: enrolled
  // with no 8821, expired, rejected, or not enrolled at all. "Not yet synced"
  // is deliberately NOT a gap — see shared/taxCoverage.
  const gaps      = entities.filter(isUnmonitored).length;
  const exempt    = entities.filter(e => authState(e).key === "exempt").length;
  const watched   = entities.length - gaps - exempt;

  // Outstanding balance across the register. Only amounts actually reported are
  // summed; a NULL is "Tax Guard did not give us a figure", and quietly adding
  // it as zero would understate the total without saying so — so the count of
  // unpaid liabilities is shown beside the money rather than instead of it.
  const unpaid    = liabilities.filter(l => !l.released && (l.amount == null || l.amount > 0));
  const unpaidAmt = unpaid.reduce((s, l) => s + (Number(l.amount) > 0 ? Number(l.amount) : 0), 0);

  // The soonest dated deadline still ahead of us. Findings with no duedate — a
  // §6672 assessment, an unpaid balance — are standing facts rather than clocks
  // and are correctly absent from this.
  const dueDays = open
    .map(w => w.duedate && Math.ceil((new Date(w.duedate) - new Date()) / 86400000))
    .filter(n => typeof n === "number" && !isNaN(n));
  const soonest = dueDays.length ? Math.min(...dueDays) : null;

  const sevCount = s => open.filter(w => (w.severity || 3) === s).length;

  // By finding type, biggest first. Same bar treatment as the covenants card's
  // categories: length is the count, scaled to the largest, so the comparison
  // the eye makes is this one against that one.
  const subjects = Object.values(open.reduce((m, w) => {
    const k = w.subject || "Other";
    (m[k] = m[k] || { subject:k, cnt:0, sev:9 });
    m[k].cnt += 1;
    m[k].sev = Math.min(m[k].sev, w.severity || 3);
    return m;
  }, {})).sort((a, b) => b.cnt - a.cnt || a.subject.localeCompare(b.subject));
  const subjMax = Math.max(1, ...subjects.map(s => s.cnt));
  const sevColor = s => (TAX_SEVERITY.find(x => x.sev === s) || TAX_SEVERITY[2]).color;

  // The coverage panel, rendered in both states. It is the reason an empty
  // worklist can be either good news or no news, and it is the only thing on
  // this card that says which.
  const coverage = (
    <div>
      <div style={subhead}>Coverage</div>
      {gaps > 0 ? (
        <div style={{ fontSize:12.5, fontWeight:600, color:"#8a2d1c", marginBottom:6 }}>
          {gaps} {gaps === 1 ? "entity" : "entities"} not monitored
        </div>
      ) : (
        <div style={{ fontSize:12.5, fontWeight:600, color:"#0F6E56", marginBottom:6 }}>
          No coverage gaps
        </div>
      )}
      <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:3 }}>
        {watched} with a current Form 8821
      </div>
      {exempt > 0 && (
        <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:3 }}>
          {exempt} not tracked by choice
        </div>
      )}
      {unpaid.length > 0 && (
        <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginTop:6 }}>
          {unpaid.length} unpaid liabilit{unpaid.length === 1 ? "y" : "ies"}
          {unpaidAmt > 0 ? " · " + dashMoney(unpaidAmt) : ""}
        </div>
      )}
    </div>
  );

  if (!total) {
    return (
      <Card style={shell}>
        <div style={title}>Tax monitoring</div>
        <div style={{ display:"grid", gap:16, gridTemplateColumns:"repeat(2, minmax(0, 1fr))" }}>
          <div>
            {/* Cyan, not amber, for the same reason the covenants card uses it:
                the amber ramp means "late" everywhere else on this page, and
                painting a clear result with it makes good news look like a
                warning. */}
            <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
              Nothing needs attention — no open lien windows, levy notices or
              lapsing authorizations.
            </div>
            <PageLink page={TAX_PAGE} style={{ display:"inline-block", marginTop:12,
                      fontSize:12, fontWeight:600, color:B.cyanDark }}>
              Open tax monitoring &rarr;
            </PageLink>
          </div>
          {coverage}
        </div>
      </Card>
    );
  }

  return (
    <Card style={shell}>
      <div style={title}>Tax monitoring</div>
      <div style={{ display:"grid", gap:16, gridTemplateColumns:"repeat(2, minmax(0, 1fr))" }}>

        {/* ── the size of it ── */}
        <div>
          <div style={{ fontSize:30, fontWeight:600, lineHeight:1,
                        color:"var(--color-text-primary)" }}>{total}</div>
          <div style={{ fontSize:13, color:"var(--color-text-secondary)", marginTop:6 }}>
            across {accounts} account{accounts === 1 ? "" : "s"}
          </div>
          {soonest != null && (
            <div style={{ fontSize:13, fontWeight:600, marginTop:4,
                          color: soonest < 0 ? "#8a2d1c" : soonest <= 30 ? "#c4611a" : "#d98a00" }}>
              {soonest < 0
                ? "soonest deadline passed " + Math.abs(soonest) + " day" + (Math.abs(soonest) === 1 ? "" : "s") + " ago"
                : "soonest deadline in " + soonest + " day" + (soonest === 1 ? "" : "s")}
            </div>
          )}
          <PageLink page={TAX_PAGE} style={{ display:"inline-block", marginTop:12,
                    fontSize:12, fontWeight:600, color:B.cyanDark }}>
            Open tax monitoring &rarr;
          </PageLink>
        </div>

        {/* ── how serious ── */}
        <div>
          <div style={subhead}>How serious</div>
          <div style={{ display:"flex", gap:2, height:10, marginBottom:10,
                        borderRadius:5, overflow:"hidden" }}>
            {TAX_SEVERITY.map(b => sevCount(b.sev) > 0 && (
              <div key={b.sev} style={{ flex:`${sevCount(b.sev)} 0 0`, background:b.color }} />
            ))}
          </div>
          {TAX_SEVERITY.map(b => (
            <div key={b.sev} style={{ display:"flex", alignItems:"center", gap:7,
                                      fontSize:12, marginBottom:5 }}>
              <span style={{ width:8, height:8, borderRadius:2, background:b.color,
                             flex:"0 0 auto" }} />
              <span style={{ fontWeight:600, color:"var(--color-text-primary)",
                             minWidth:26 }}>{sevCount(b.sev)}</span>
              <span style={{ color:"var(--color-text-secondary)" }}>{b.label}</span>
            </div>
          ))}
        </div>

        {/* ── what kind ── */}
        <div>
          <div style={subhead}>By finding</div>
          {subjects.slice(0, 4).map(s => (
            <div key={s.subject} style={{ marginBottom:7 }}>
              <div style={{ display:"flex", justifyContent:"space-between",
                            fontSize:12, marginBottom:3, gap:8 }}>
                <span style={{ color:"var(--color-text-primary)", overflow:"hidden",
                               textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                      title={s.subject}>{s.subject}</span>
                <span style={{ fontWeight:600, flex:"0 0 auto" }}>{s.cnt}</span>
              </div>
              <div style={{ height:6, borderRadius:3,
                            background:"var(--color-background-secondary)" }}>
                <div style={{ height:6, borderRadius:3,
                              width:`${Math.max(4, (s.cnt / subjMax) * 100)}%`,
                              background:sevColor(s.sev) }} />
              </div>
            </div>
          ))}
          {subjects.length > 4 && (
            <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginTop:6 }}>
              + {subjects.slice(4).reduce((s, x) => s + x.cnt, 0)} in{" "}
              {subjects.length - 4} other kind{subjects.length - 4 === 1 ? "" : "s"}
            </div>
          )}
        </div>

        {coverage}

      </div>
    </Card>
  );
};

const subhead = { fontSize:10.5, fontWeight:700, color:"var(--color-text-secondary)",
                  textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 };

// A money figure for a card: whole dollars. shared/format's `fmt` carries cents,
// which belongs in a column you will reconcile and not beside a headline.
const dashMoney = n => "$" + Math.round(Number(n) || 0).toLocaleString("en-US");

// For a figure set as a headline. "$41,200,000" at 32px is a SINGLE UNBREAKABLE
// TOKEN about 225px wide, and the column it sits in narrows to 186px of content
// — flex-wrap cannot help, because there is nowhere to wrap a word. So past a
// million it reads $41.2M, and the exact figure still appears in the legend row
// underneath. Below a million the full number fits and is kept: $412,500 says
// something $0.4M does not.
const headlineMoney = (n) => {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) < 1e6) return "$" + v.toLocaleString("en-US");
  const m = v / 1e6;
  return "$" + (Math.abs(m) >= 100 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, "")) + "M";
};

const reportRow = { display:"flex", justifyContent:"space-between", alignItems:"baseline",
                    fontSize:12, marginBottom:6, gap:8 };

// ── Owed to participants ───────────────────────────────────────────────────
//
// The mirror of the Delinquency card, pointing the other way: this is money WE
// owe, not money owed to us. That is the whole reason it is a separate tile and
// not another row on an existing one — a reader glancing at a dashboard should
// never have to work out which direction a number points.
//
// So the money is the headline here rather than the count. "We owe $412,000" is
// the fact; how many payments that is across is the detail.
export const OwedToParticipants = ({ p, title }) => {
  const d = p || {};
  const owed = d.owedcnt || 0, soon = d.soonCnt || 0;
  // Payments past due on assets in non-accrual. Listed on the report, not in
  // the count: interest stops being owed to the participant from that date.
  const naCnt = Number(d.nacnt) || 0, naAmt = Number(d.naamount) || 0;
  const nothing = owed === 0 && soon === 0;

  // Now vs next-30 on one bar. Both are money we owe; the split is only whether
  // the date has passed. Late money is the darker step of the same ramp the two
  // aging cards use, so "darker means later" holds across the whole page.
  const LATE = AGE_BANDS[2].color, SOON = "#0e93b0";
  const owedAmt = Number(d.owedamount) || 0, soonAmt = Number(d.soonAmount) || 0;

  return (
    <PageLink page="owed-payments" style={{ display:"block", height:"100%" }} aria-label={nothing
      ? "Owed and Upcoming Payments report — nothing owed"
      : `Owed and Upcoming Payments report — ${owed} payment${owed === 1 ? "" : "s"} owed now, ${soon} due in the next 30 days`}>
      <Card style={{ padding:16, height:"100%", boxSizing:"border-box",
                     transition:"box-shadow .12s, transform .12s" }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.14)";
                                 e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "";
                                 e.currentTarget.style.transform = ""; }}>
        <div style={title}>Owed to participants</div>

        {nothing ? (
          <>
            <div style={{ fontSize:13, color:"var(--color-text-secondary)", marginBottom:10 }}>
              Nothing owed or due in the next 30 days.
            </div>
            {naCnt > 0 && (
              <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:10 }}>
                {naCnt} payment{naCnt === 1 ? "" : "s"} ({dashMoney(naAmt)}) on non-accrual assets &mdash; on the report, not in the count.
              </div>
            )}
            <div style={{ fontSize:12, fontWeight:600, color:B.cyanDark }}>
              Open the report &rarr;
            </div>
          </>
        ) : (
          <>
            {/* LEAD WITH THE NUMBER THAT MATTERS. When nothing is past due
                the honest headline is what is coming, not a $0 — a card whose
                biggest text is zero while $730,000 sits in the legend below has
                buried its own story. */}
            <div style={{ display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap" }}>
              <span style={{ fontSize:32, fontWeight:600, lineHeight:1,
                             color:"var(--color-text-primary)" }}>
                {headlineMoney(owed > 0 ? owedAmt : soonAmt)}</span>
              <span style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
                {owed > 0 ? "owed now" : "due in the next 30 days"}
              </span>
            </div>
            {owed > 0 && (
              <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginTop:5 }}>
                {owed} payment{owed === 1 ? "" : "s"}
                {d.oldestdays > 0 && <>, oldest <span style={{ color:bandFor(d.oldestdays).color,
                  fontWeight:600 }}>{d.oldestdays} day{d.oldestdays === 1 ? "" : "s"} late</span></>}
              </div>
            )}

            {/* flex-grow so the 2px gap is taken out of the available width
                rather than pushing the bar past its container. */}
            <div style={{ display:"flex", gap:2, height:8, marginTop:12, marginBottom:9,
                          borderRadius:4, overflow:"hidden" }}>
              {owedAmt > 0 && <div style={{ flex:`${owedAmt} 0 0`, background:LATE }} />}
              {soonAmt > 0 && <div style={{ flex:`${soonAmt} 0 0`, background:SOON }} />}
            </div>

            <div style={{ display:"flex", alignItems:"center", gap:7, fontSize:12, marginBottom:4 }}>
              <span style={{ width:8, height:8, borderRadius:2, background:LATE, flex:"0 0 auto" }} />
              <span style={{ fontWeight:600 }}>{dashMoney(owedAmt)}</span>
              <span style={{ color:"var(--color-text-secondary)" }}>past due</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:7, fontSize:12, marginBottom:9 }}>
              <span style={{ width:8, height:8, borderRadius:2, background:SOON, flex:"0 0 auto" }} />
              <span style={{ fontWeight:600 }}>{dashMoney(soonAmt)}</span>
              <span style={{ color:"var(--color-text-secondary)" }}>
                next 30 days{soon > 0 ? ` · ${soon} pmt${soon === 1 ? "" : "s"}` : ""}
              </span>
            </div>
            {naCnt > 0 && (
              <div style={{ fontSize:12, color:"var(--color-text-secondary)", marginBottom:9 }}>
                + {naCnt} on non-accrual ({dashMoney(naAmt)}) &mdash; not in the count
              </div>
            )}

            <div style={{ fontSize:12, fontWeight:600, color:B.cyanDark }}>
              Open the report &rarr;
            </div>
          </>
        )}
      </Card>
    </PageLink>
  );
};
